import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { LogoTextShape, loginforgepdf } from "../assets";
import { getAuthErrorMessage } from "../utils/errorMessages";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../config/firebase";

export default function Login() {
  const { signInWithGoogle, signInWithEmail, signUpWithEmail, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const initializeWallet = async (userId: string) => {
    try {
      const walletRef = doc(db, "wallet", userId);
      const walletDoc = await getDoc(walletRef);

      if (!walletDoc.exists()) {
        const userRef = doc(db, "users", userId);
        await setDoc(walletRef, {
          user_ref: userRef,
          free_usage: 0,
          free_usage_limit: 1000,
          token_usage: 0,
          amount: 0,
          available_tokens: 0,
          created_at: new Date(),
          updated_at: new Date(),
        });
        console.log("Wallet created successfully for user:", userId);
      }
    } catch (error) {
      console.error("Error initializing wallet:", error);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const userCredential = await signInWithGoogle();
      if (userCredential.user) {
        await initializeWallet(userCredential.user.uid);
      }
      navigate("/dashboard");
    } catch (error) {
      console.error("Login error:", error);
      setError(getAuthErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setError(null);
      const userCredential = isSignUp
        ? await signUpWithEmail(email, password)
        : await signInWithEmail(email, password);
      if (userCredential.user) {
        await initializeWallet(userCredential.user.uid);
      }
      navigate("/dashboard");
    } catch (error) {
      console.error("Authentication error:", error);
      setError(getAuthErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setError(null);
      await resetPassword(email);
      setResetEmailSent(true);
    } catch (error) {
      console.error("Password reset error:", error);
      setError(getAuthErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <>
      {/* Add responsive styles */}
      <style>{`
        * {
          box-sizing: border-box;
        }
        .login-container {
          display: flex;
          height: 100vh;
          width: 100vw;
          backgroundColor: #ffffff;
        }
        .login-left-panel {
          flex: 0 0 50%;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 80px 120px;
          position: relative;
        }
        .login-right-panel {
          flex: 0 0 50%;
          background: #7637FF;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 12px;
          margin: 16px;
          position: relative;
          overflow: hidden;
          border-radius: 24px;
        }
        @media (max-width: 1024px) {
          .login-left-panel {
            flex: 1;
            padding: 60px 40px;
          }
          .login-right-panel {
            display: none !important;
          }
        }
        @media (max-width: 768px) {
          .login-left-panel {
            padding: 40px 24px;
          }
          .login-logo {
            position: static !important;
            margin-bottom: 40px;
          }
        }
      `}</style>

      <div className="login-container">
        {/* Left Panel */}
        <div className="login-left-panel">
          {/* Logo */}
          <div className="login-logo" style={{ position: "absolute", top: "40px", left: "80px" }}>
            <img
              src={LogoTextShape}
              alt="Forge PDF"
              style={{
                height: "40px",
              }}
            />
          </div>

          {/* Main Content */}
          <div style={{ maxWidth: "400px", width: "100%" }}>
            {/* Heading */}
            <div style={{ marginBottom: "48px" }}>
              <h1
                style={{
                  fontSize: "48px",
                  fontWeight: "700",
                  color: "#1a1a1a",
                  lineHeight: "1.2",
                  marginBottom: "12px",
                }}
              >
                {isForgotPassword ? (
                  <>
                    Reset Your
                    <br />
                    Password
                  </>
                ) : (
                  <>
                    {isSignUp ? "Hello," : "Hello,"}
                    <br />
                    {isSignUp ? "Create Account" : "Welcome Back"}
                  </>
                )}
              </h1>
              <p
                style={{
                  fontSize: "15px",
                  color: "#9ca3af",
                  fontWeight: "400",
                }}
              >
                {isForgotPassword
                  ? resetEmailSent
                    ? "We've sent you an email with instructions"
                    : "Enter your email to receive a reset link"
                  : isSignUp
                    ? "Get started with your special place"
                    : "Hey, welcome back to your special place"}
              </p>
            </div>

            {error && (
              <div
                style={{
                  marginBottom: "24px",
                  padding: "12px 16px",
                  backgroundColor: "#fee2e2",
                  borderRadius: "8px",
                  color: "#dc2626",
                  fontSize: "14px",
                }}
              >
                {error}
              </div>
            )}

            {/* Form */}
            {isForgotPassword ? (
              // Forgot Password Form
              <>
                {!resetEmailSent ? (
                  <form onSubmit={handlePasswordReset}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                      {/* Email Input */}
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="stanley@gmail.com"
                        required
                        style={{
                          width: "100%",
                          padding: "16px 20px",
                          fontSize: "15px",
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          outline: "none",
                          transition: "all 0.2s",
                          backgroundColor: "#fafafa",
                          boxSizing: "border-box",
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = "#7C3AED";
                          e.target.style.backgroundColor = "#ffffff";
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = "#e5e7eb";
                          e.target.style.backgroundColor = "#fafafa";
                        }}
                      />

                      {/* Send Reset Link Button */}
                      <button
                        type="submit"
                        disabled={isLoading}
                        style={{
                          width: "100%",
                          padding: "16px",
                          fontSize: "16px",
                          fontWeight: "600",
                          color: "#ffffff",
                          backgroundColor: isLoading ? "#a78bfa" : "#7C3AED",
                          border: "none",
                          borderRadius: "8px",
                          cursor: isLoading ? "not-allowed" : "pointer",
                          marginTop: "8px",
                          transition: "all 0.3s",
                          boxSizing: "border-box",
                        }}
                        onMouseOver={(e) => {
                          if (!isLoading) e.currentTarget.style.backgroundColor = "#6d28d9";
                        }}
                        onMouseOut={(e) => {
                          if (!isLoading) e.currentTarget.style.backgroundColor = "#7C3AED";
                        }}
                      >
                        {isLoading ? "Sending..." : "Send Reset Link"}
                      </button>

                      {/* Back to Sign In */}
                      <button
                        type="button"
                        onClick={() => {
                          setIsForgotPassword(false);
                          setError(null);
                        }}
                        style={{
                          width: "100%",
                          padding: "16px",
                          fontSize: "15px",
                          fontWeight: "500",
                          color: "#374151",
                          backgroundColor: "#ffffff",
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          boxSizing: "border-box",
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.backgroundColor = "#f9fafb";
                          e.currentTarget.style.borderColor = "#d1d5db";
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = "#ffffff";
                          e.currentTarget.style.borderColor = "#e5e7eb";
                        }}
                      >
                        Back to Sign In
                      </button>
                    </div>
                  </form>
                ) : (
                  // Success Message
                  <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    {/* Success Icon */}
                    <div
                      style={{
                        width: "64px",
                        height: "64px",
                        borderRadius: "50%",
                        backgroundColor: "#dcfce7",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 16px",
                      }}
                    >
                      <svg
                        width="32"
                        height="32"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#16a34a"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                    </div>

                    {/* Success Message */}
                    <div
                      style={{
                        padding: "20px",
                        backgroundColor: "#f0fdf4",
                        borderRadius: "8px",
                        border: "1px solid #bbf7d0",
                        textAlign: "center",
                      }}
                    >
                      <p style={{ fontSize: "15px", color: "#166534", lineHeight: "1.6", margin: 0 }}>
                        Check your inbox at <strong>{email}</strong>. We've sent you a link to reset your password.
                      </p>
                    </div>

                    {/* Back to Sign In Button */}
                    <button
                      type="button"
                      onClick={() => {
                        setIsForgotPassword(false);
                        setResetEmailSent(false);
                        setError(null);
                      }}
                      style={{
                        width: "100%",
                        padding: "16px",
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#ffffff",
                        backgroundColor: "#7C3AED",
                        border: "none",
                        borderRadius: "8px",
                        cursor: "pointer",
                        marginTop: "8px",
                        transition: "all 0.3s",
                        boxSizing: "border-box",
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = "#6d28d9";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = "#7C3AED";
                      }}
                    >
                      Back to Sign In
                    </button>
                  </div>
                )}
              </>
            ) : (
              // Regular Sign In/Sign Up Form
              <form onSubmit={handleEmailAuth}>
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  {/* Email Input */}
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="stanley@gmail.com"
                    required
                    style={{
                      width: "100%",
                      padding: "16px 20px",
                      fontSize: "15px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      outline: "none",
                      transition: "all 0.2s",
                      backgroundColor: "#fafafa",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#7C3AED";
                      e.target.style.backgroundColor = "#ffffff";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e5e7eb";
                      e.target.style.backgroundColor = "#fafafa";
                    }}
                  />

                  {/* Password Input */}
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••••"
                    required
                    style={{
                      width: "100%",
                      padding: "16px 20px",
                      fontSize: "15px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      outline: "none",
                      transition: "all 0.2s",
                      backgroundColor: "#fafafa",
                      letterSpacing: "2px",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#7C3AED";
                      e.target.style.backgroundColor = "#ffffff";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e5e7eb";
                      e.target.style.backgroundColor = "#fafafa";
                    }}
                  />

                  {/* Remember Me & Forgot Password */}
                  {!isSignUp && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          cursor: "pointer",
                          fontSize: "14px",
                          color: "#6b7280",
                        }}
                      >
                        <input
                          type="checkbox"
                          style={{
                            width: "18px",
                            height: "18px",
                            accentColor: "#7C3AED",
                            cursor: "pointer",
                          }}
                        />
                        Remember me
                      </label>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setIsForgotPassword(true);
                          setError(null);
                        }}
                        style={{
                          fontSize: "14px",
                          color: "#9ca3af",
                          textDecoration: "none",
                        }}
                      >
                        Forgot Password?
                      </a>
                    </div>
                  )}

                  {/* Sign In Button */}
                  <button
                    type="submit"
                    disabled={isLoading}
                    style={{
                      width: "100%",
                      padding: "16px",
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#ffffff",
                      backgroundColor: isLoading ? "#a78bfa" : "#7C3AED",
                      border: "none",
                      borderRadius: "8px",
                      cursor: isLoading ? "not-allowed" : "pointer",
                      marginTop: "8px",
                      transition: "all 0.3s",
                      boxSizing: "border-box",
                    }}
                    onMouseOver={(e) => {
                      if (!isLoading) e.currentTarget.style.backgroundColor = "#6d28d9";
                    }}
                    onMouseOut={(e) => {
                      if (!isLoading) e.currentTarget.style.backgroundColor = "#7C3AED";
                    }}
                  >
                    {isLoading
                      ? "Please wait..."
                      : isSignUp
                        ? "Create Account"
                        : "Sign In"}
                  </button>
                </div>
              </form>
            )}

            {/* Divider - Only show for regular form */}
            {!isForgotPassword && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  margin: "32px 0",
                }}
              >
                <div style={{ flex: 1, height: "1px", backgroundColor: "#e5e7eb" }} />
                <span style={{ padding: "0 16px", color: "#9ca3af", fontSize: "14px" }}>
                  or
                </span>
                <div style={{ flex: 1, height: "1px", backgroundColor: "#e5e7eb" }} />
              </div>
            )}

            {/* Google Sign In - Only show for regular form */}
            {!isForgotPassword && (
              <button
                onClick={handleGoogleSignIn}
                disabled={isLoading}
                style={{
                  width: "100%",
                  padding: "16px",
                  fontSize: "15px",
                  fontWeight: "500",
                  color: "#374151",
                  backgroundColor: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  cursor: isLoading ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "12px",
                  transition: "all 0.2s",
                  boxSizing: "border-box",
                }}
                onMouseOver={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.backgroundColor = "#f9fafb";
                    e.currentTarget.style.borderColor = "#d1d5db";
                  }
                }}
                onMouseOut={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.backgroundColor = "#ffffff";
                    e.currentTarget.style.borderColor = "#e5e7eb";
                  }
                }}
              >
                <img
                  src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                  alt="Google"
                  style={{ width: "20px", height: "20px" }}
                />
                Continue with Google
              </button>
            )}

            {/* Sign Up Link - Only show for regular form */}
            {!isForgotPassword && (
              <div style={{ textAlign: "center", marginTop: "32px" }}>
                <span style={{ fontSize: "14px", color: "#9ca3af" }}>
                  {isSignUp ? "Already have an account? " : "Don't have an account? "}
                </span>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsSignUp(!isSignUp);
                  }}
                  style={{
                    fontSize: "14px",
                    color: "#7C3AED",
                    textDecoration: "none",
                    fontWeight: "600",
                  }}
                >
                  {isSignUp ? "Sign In" : "Sign Up"}
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Illustration */}
        <div className="login-right-panel">
          {/* Central Illustration */}
          <img
            src={loginforgepdf}
            alt="Forge PDF login illustration"
            style={{
              width: "100%",
              height: "auto",
              maxWidth: "600px",
              objectFit: "contain",
            }}
          />
        </div>
      </div>
    </>
  );
}
